import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
    Bold, Italic, Underline, List, ListOrdered, Quote, Code, Heading1, Heading2,
    Heading3, Link as LinkIcon, Image as ImageIcon, Minus, Undo2, Redo2, Pilcrow,
} from "lucide-react";

/**
 * TipTap WYSIWYG editor used in /admin/blog. Emits sanitized HTML via the
 * `onChange(html)` prop; the backend persists exactly what the editor emits
 * and the public view runs the string through DOMPurify (`safeHtml`) again
 * before rendering.
 */
const Tb = ({ onClick, active, icon: Icon, title, testId }) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        data-testid={testId}
        onClick={onClick}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
            active
                ? "bg-brand-navy text-white border-brand-navy dark:bg-slate-800"
                : "bg-card text-foreground border-border hover:border-brand-navy hover:bg-accent"
        }`}
    >
        <Icon size={14} />
    </button>
);

export const BlogTiptapEditor = ({ value, onChange, placeholder = "Write your article…" }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
            }),
            Image.configure({ inline: false, allowBase64: false }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        editorProps: {
            attributes: {
                class: "prose prose-slate dark:prose-invert max-w-none min-h-[320px] p-4 focus:outline-none",
                "data-testid": "blog-editor-surface",
            },
        },
        onUpdate: ({ editor: ed }) => {
            onChange?.(ed.getHTML());
        },
    });

    // Keep the editor content in sync when the parent form resets or loads
    // a different post for editing. Only runs when `value` changes AND the
    // current HTML doesn't already match (prevents cursor jumps on typing).
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value !== undefined && value !== null && value !== current) {
            editor.commands.setContent(value || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) return null;

    const setLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("URL (leave empty to remove link):", prev);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const addImage = () => {
        const url = window.prompt("Image URL:");
        if (!url) return;
        editor.chain().focus().setImage({ src: url, alt: "" }).run();
    };

    return (
        <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid="blog-tiptap-editor">
            <div className="flex flex-wrap items-center gap-1.5 p-2 border-b border-border bg-muted/40">
                <Tb testId="tb-undo" title="Undo" icon={Undo2} onClick={() => editor.chain().focus().undo().run()} />
                <Tb testId="tb-redo" title="Redo" icon={Redo2} onClick={() => editor.chain().focus().redo().run()} />
                <span className="mx-1 h-5 w-px bg-border" />
                <Tb testId="tb-para" title="Paragraph" icon={Pilcrow} active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} />
                <Tb testId="tb-h1" title="Heading 1" icon={Heading1} active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} />
                <Tb testId="tb-h2" title="Heading 2" icon={Heading2} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
                <Tb testId="tb-h3" title="Heading 3" icon={Heading3} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
                <span className="mx-1 h-5 w-px bg-border" />
                <Tb testId="tb-bold" title="Bold" icon={Bold} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} />
                <Tb testId="tb-italic" title="Italic" icon={Italic} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} />
                <Tb testId="tb-strike" title="Strikethrough" icon={Underline} active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} />
                <span className="mx-1 h-5 w-px bg-border" />
                <Tb testId="tb-ul" title="Bulleted list" icon={List} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} />
                <Tb testId="tb-ol" title="Numbered list" icon={ListOrdered} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
                <Tb testId="tb-quote" title="Blockquote" icon={Quote} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
                <Tb testId="tb-code" title="Code block" icon={Code} active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
                <Tb testId="tb-hr" title="Horizontal rule" icon={Minus} onClick={() => editor.chain().focus().setHorizontalRule().run()} />
                <span className="mx-1 h-5 w-px bg-border" />
                <Tb testId="tb-link" title="Link" icon={LinkIcon} active={editor.isActive("link")} onClick={setLink} />
                <Tb testId="tb-image" title="Insert image URL" icon={ImageIcon} onClick={addImage} />
            </div>
            <EditorContent editor={editor} />
        </div>
    );
};

export default BlogTiptapEditor;
